// #2611 — single source of truth for the canonical SDLC phase vocabulary.
// Before this, the same phase_N_* list existed independently in RisksPage.tsx's
// filterPhase dropdown (a stale, partial 5-of-10 copy) and its carry-forward
// "Target phase" field (free text, no vocabulary at all — the bug this closes).
// Authoritative spelling matches phase-gate-agent.ts's WAIVER_PHASE_DISPLAY and
// PhaseBadge.tsx's phaseConfig (both apps already agree; neither imported from
// the other). Both the API (apps/api) and the Web app (apps/web) import from
// here — see packages/shared/src/breakpoints.ts for the same cross-app pattern.

export const CANONICAL_PHASES = [
  'phase_0_bootstrap',
  'phase_1_requirements',
  'phase_2_architecture',
  'phase_2_5_ux_design',
  'phase_3_sprint_planning',
  'phase_4_development',
  'phase_5_code_review',
  'phase_6_testing',
  'phase_7_deployment',
  'phase_8_retrospective',
] as const;

export type CanonicalPhase = (typeof CANONICAL_PHASES)[number];

export const CANONICAL_PHASE_LABELS: Record<CanonicalPhase, string> = {
  phase_0_bootstrap:       'Bootstrap',
  phase_1_requirements:    'Requirements',
  phase_2_architecture:    'Architecture',
  phase_2_5_ux_design:     'UX Design',
  phase_3_sprint_planning: 'Sprint Planning',
  phase_4_development:     'Development',
  phase_5_code_review:     'Code Review',
  phase_6_testing:         'Testing',
  phase_7_deployment:      'Deployment',
  phase_8_retrospective:   'Retrospective',
};

export function isCanonicalPhase(value: string | null | undefined): value is CanonicalPhase {
  return !!value && (CANONICAL_PHASES as readonly string[]).includes(value);
}

/** Human label for a canonical phase key; returns the input unchanged for a
 *  legacy free-text value (e.g. a pre-#2611 carried-forward risk's phase)
 *  rather than mangling it — display must not break on old data. */
export function canonicalPhaseLabel(phase: string): string {
  return isCanonicalPhase(phase) ? CANONICAL_PHASE_LABELS[phase] : phase;
}
